import { useState, useEffect, useCallback } from 'react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Search, Eye, Star, Sparkles, ChevronRight, Globe, FolderTree, Layers } from 'lucide-react'
import { CASES_DATA } from '@/data/cases'
import { SCOPES, INSTITUTION_TREE, CATEGORIES, SCENARIOS } from '@/data/shared-templates'
import type { CaseData } from '@/data/cases'
import type { InstitutionNode } from '@/data/shared-templates'
import { cn } from '@/lib/utils'

interface CasesPageProps {
  onCaseDetail: (caseId: string) => void
}

const COVER_GRADIENTS = [
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-purple-500 to-pink-600',
  'from-red-500 to-rose-600',
  'from-cyan-500 to-blue-600',
]

const COVER_ICONS = ['📊', '📈', '🛡️', '', '🔍', '📋']

export function CasesPage({ onCaseDetail, showFavoritesOnly: initialShowFavorites }: CasesPageProps & { showFavoritesOnly?: boolean }) {
  const [keyword, setKeyword] = useState('')
  const [activeScope, setActiveScope] = useState<string>('all')
  const [selectedInstitution, setSelectedInstitution] = useState<InstitutionNode | null>(null)
  const [showInstitutionDialog, setShowInstitutionDialog] = useState(false)
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [activeScenario, setActiveScenario] = useState<string | null>(null)
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('case_favorites')
      if (saved) {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed)) return new Set(parsed)
      }
    } catch {
      // ignore
    }
    return new Set<string>()
  })

  useEffect(() => {
    localStorage.setItem('case_favorites', JSON.stringify([...favorites]))
  }, [favorites])

  const toggleFavorite = useCallback((id: string) => {
    setFavorites(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const [showFavoritesOnly, setShowFavoritesOnly] = useState(initialShowFavorites ?? false)

  // 查找机构路径名
  function findInstitutionPath(targetId: string, nodes: InstitutionNode[] = INSTITUTION_TREE, path: string = ''): string {
    for (const node of nodes) {
      const currentPath = path ? `${path} / ${node.name}` : node.name
      if (node.id === targetId) return currentPath
      if (node.children) {
        const found = findInstitutionPath(targetId, node.children, currentPath)
        if (found) return found
      }
    }
    return ''
  }

  const filtered = CASES_DATA.filter(c => {
    if (showFavoritesOnly && !favorites.has(c.id)) return false
    if (keyword) {
      const k = keyword.toLowerCase()
      if (
        !c.name.toLowerCase().includes(k) &&
        !c.intro.toLowerCase().includes(k) &&
        !c.keywords.some(kw => kw.toLowerCase().includes(k))
      ) return false
    }
    if (activeScope === 'mine') return false
    if (activeScope === 'headquarters') return false
    if (activeScope === 'branch' && c.institution !== '总行') return false
    if (activeScope === 'subbranch') return false
    if (activeScope === 'institution' && selectedInstitution) {
      const instName = findInstitutionPath(selectedInstitution.id)
      if (c.institution !== '总行' && !instName.includes(c.institution)) return false
    }
    if (activeCategory && c.businessLine !== activeCategory) return false
    if (activeScenario && c.scenario !== activeScenario) return false
    return true
  })

  const handleSelectInstitution = (inst: InstitutionNode) => {
    if (!inst.children) {
      setSelectedInstitution(inst)
      setShowInstitutionDialog(false)
    }
  }

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="p-6">
        {/* 顶部标题 */}
        <div className="mb-6">
          <h1 className="text-lg font-semibold text-gray-900">经典案例</h1>
          <p className="text-sm text-gray-500 mt-1">浏览全行优秀数据分析案例，学习分析思路，复用优秀模板</p>
        </div>

        {/* 搜索框 */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="搜索案例名称、关键词..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="h-10 pl-10 pr-4 text-sm rounded-md border border-gray-300 focus:border-gcb-primary focus:outline-none focus:ring-1 focus:ring-gcb-primary"
            />
          </div>
        </div>

        {/* 筛选栏 */}
        <div className="bg-white rounded-lg border p-4 mb-4 space-y-3">
          {/* 范围 */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16 shrink-0 flex items-center gap-1">
              <Globe className="w-3 h-3" /> 范围：
            </span>
            <div className="flex gap-1.5 flex-wrap">
              {SCOPES.map(s => (
                <FilterChip
                  key={s.value}
                  label={s.label}
                  active={activeScope === s.value}
                  onClick={() => {
                    setActiveScope(s.value)
                    if (s.value === 'institution') {
                      setShowInstitutionDialog(true)
                    } else {
                      setSelectedInstitution(null)
                    }
                  }}
                />
              ))}
              {activeScope === 'institution' && selectedInstitution && (
                <Badge variant="secondary" className="text-xs px-2 py-0.5 bg-blue-50 text-gcb-primary border-gcb-primary/20">
                  {findInstitutionPath(selectedInstitution.id)}
                  <button
                    className="ml-1 text-gcb-primary hover:text-gcb-primaryDark"
                    onClick={() => { setSelectedInstitution(null); setActiveScope('all') }}
                  >
                    ×
                  </button>
                </Badge>
              )}
            </div>
            <button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              className={`px-2.5 py-1 rounded-md text-xs transition-all flex items-center gap-1 ml-2 ${
                showFavoritesOnly
                  ? 'bg-gcb-primary text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Star className={`w-3 h-3 ${showFavoritesOnly ? 'fill-white' : ''}`} />
              我收藏的
              <span className="text-[10px] opacity-60">{favorites.size}</span>
            </button>
          </div>
          {/* 业务 */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16 shrink-0 flex items-center gap-1">
              <FolderTree className="w-3 h-3" /> 业务：
            </span>
            <div className="flex gap-1.5 flex-wrap">
              <FilterChip label="全部" active={!activeCategory} onClick={() => setActiveCategory(null)} />
              {CATEGORIES.map(cat => (
                <FilterChip key={cat} label={cat} active={activeCategory === cat} onClick={() => setActiveCategory(cat)} />
              ))}
            </div>
          </div>
          {/* 场景 */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16 shrink-0 flex items-center gap-1">
              <Layers className="w-3 h-3" /> 场景：
            </span>
            <div className="flex gap-1.5 flex-wrap">
              <FilterChip label="全部" active={!activeScenario} onClick={() => setActiveScenario(null)} />
              {SCENARIOS.map(s => (
                <FilterChip key={s} label={s} active={activeScenario === s} onClick={() => setActiveScenario(s)} />
              ))}
            </div>
          </div>
        </div>

        {/* 统计信息 */}
        <div className="text-xs text-gray-500 mb-3">
          共 <span className="font-medium text-gray-900">{filtered.length}</span> 个案例
        </div>

        {/* 案例卡片网格 */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-lg border text-center py-16 text-gray-400 text-sm">
            暂无匹配的案例
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((c, idx) => (
              <CaseCard key={c.id} caseData={c} gradient={COVER_GRADIENTS[idx % COVER_GRADIENTS.length]} icon={COVER_ICONS[idx % COVER_ICONS.length]} onClick={() => onCaseDetail(c.id)} favorites={favorites} onToggleFavorite={toggleFavorite} />
            ))}
          </div>
        )}
      </div>

      {/* 机构选择弹窗 */}
      <Dialog open={showInstitutionDialog} onOpenChange={setShowInstitutionDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>选择机构</DialogTitle>
            <DialogDescription>请选择需要筛选的分支机构</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <InstitutionTree
              nodes={INSTITUTION_TREE}
              selectedId={selectedInstitution?.id}
              onSelect={handleSelectInstitution}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/* ---- Filter Chip ---- */
function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 rounded-md text-xs transition-all',
        active
          ? 'bg-gcb-primary text-white shadow-sm'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      )}
    >
      {label}
    </button>
  )
}

/* ---- Institution Tree ---- */
function InstitutionTree({
  nodes,
  selectedId,
  onSelect,
  level = 0,
}: {
  nodes: InstitutionNode[]
  selectedId?: string
  onSelect: (node: InstitutionNode) => void
  level?: number
}) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['hq']))

  const toggleExpand = (id: string, hasChildren: boolean) => {
    if (!hasChildren) return
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-1">
      {nodes.map(node => {
        const isExpanded = expandedIds.has(node.id)
        const isSelected = selectedId === node.id
        const hasChildren = !!(node.children && node.children.length > 0)

        return (
          <div key={node.id}>
            <button
              className={`w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm transition-colors text-left ${
                isSelected
                  ? 'bg-blue-100 text-gcb-primaryDark font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              style={{ paddingLeft: `${level * 16 + 8}px` }}
              onClick={() => {
                toggleExpand(node.id, hasChildren)
                onSelect(node)
              }}
            >
              {hasChildren ? (
                <ChevronRight
                  className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                />
              ) : (
                <span className="w-3.5 shrink-0" />
              )}
              <span className={hasChildren ? 'font-medium' : ''}>{node.name}</span>
            </button>
            {hasChildren && isExpanded && (
              <InstitutionTree
                nodes={node.children!}
                selectedId={selectedId}
                onSelect={onSelect}
                level={level + 1}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

/* ---- Case Card ---- */
function CaseCard({
  caseData: c,
  gradient,
  icon,
  onClick,
  favorites,
  onToggleFavorite,
}: {
  caseData: CaseData
  gradient: string
  icon: string
  onClick: () => void
  favorites: Set<string>
  onToggleFavorite: (id: string) => void
}) {
  return (
    <div className="bg-white rounded-lg border overflow-hidden hover:shadow-md transition-shadow group cursor-pointer" onClick={onClick}>
      {/* 封面 */}
      <div className={cn('h-32 bg-gradient-to-br flex items-center justify-center relative', gradient)}>
        <span className="text-5xl">{icon}</span>
        {/* 访问量 */}
        <div className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
          <Eye className="w-3 h-3" />
          {c.viewCount}
        </div>
      </div>

      {/* 内容 */}
      <div className="p-4">
        {/* 标题 */}
        <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-1 group-hover:text-gcb-primary transition-colors">
          {c.name}
        </h3>

        {/* 简介 */}
        <p className="text-xs text-gray-500 mb-3 line-clamp-2 leading-relaxed">{c.intro}</p>

        {/* 业务成效 */}
        <div className="bg-green-50/60 border border-green-100 rounded-md p-2 mb-3">
          <div className="flex items-center gap-1 mb-1">
            <Sparkles className="w-3 h-3 text-green-600" />
            <span className="text-[10px] font-medium text-green-700">业务成效</span>
          </div>
          <p className="text-[11px] text-green-800 line-clamp-2">{c.businessResults}</p>
        </div>

        {/* 标签 */}
        <div className="flex flex-wrap gap-1 mb-3">
          <Badge variant="outline" className="text-[10px] h-4 px-1">{c.businessLine}</Badge>
          <Badge className="text-[10px] h-4 px-1 bg-green-50 text-green-700 border-green-200">{c.scenario}</Badge>
        </div>

        {/* 底部信息 */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-3 text-[11px] text-gray-400">
            <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{c.viewCount}</span>
            <span className="flex items-center gap-1"><Star className="w-3 h-3" />{c.likeCount}</span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(c.id) }}
            className={`transition-colors ${
              favorites.has(c.id)
                ? 'text-yellow-500 hover:text-yellow-600'
                : 'text-gray-300 hover:text-yellow-400'
            }`}
            title={favorites.has(c.id) ? '取消收藏' : '收藏'}
          >
            <Star className={`w-4 h-4 ${favorites.has(c.id) ? 'fill-yellow-500' : ''}`} />
          </button>
        </div>
      </div>
    </div>
  )
}
